import { WarningCircleIcon } from "@phosphor-icons/react/dist/ssr/WarningCircle";
import { clsx } from "clsx";
import { useState } from "react";
import reactStringReplace from "react-string-replace";

import ChangeDate from "./ChangeDate";
import DateDisplay from "./DateDisplay";
import { formatCents } from "./format-cents";
import LineItemConfirmDelete from "./LineItemConfirmDelete";
import { MoneyTable } from "./MoneyTable";

import { logError } from "@up/data";

import type { TGalaxyError, TLineItem } from "@up/data";

type Props = {
  canChangeDate?: boolean;
  errors?: Array<TGalaxyError>;
  item: TLineItem;
  removeFn?: (_id: string) => Promise<unknown>;
};

export function LineItem({
  canChangeDate = false,
  errors,
  item,
  removeFn,
}: Props) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [changeDate, setChangeDate] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [didError, setDidError] = useState(false);

  const handleRemove = async function () {
    if (removeFn) {
      setIsDeleting(true);
      setDidError(false);
      try {
        await removeFn(item._id);
      } catch (err) {
        setIsDeleting(false);
        setDidError(true);
        logError({
          action: "CHECKOUT_REMOVE_ITEM",
          error: err,
          context: { itemId: item._id },
        });
      }

      setIsDeleting(false);
    }
  };

  const removeStep1 = function () {
    setChangeDate(false);
    setConfirmDelete(true);
  };

  return (
    <li
      className={clsx(
        "animation-fade-in checkout-list-item relative rounded",
        confirmDelete
          ? "confirm-removal"
          : changeDate
            ? "change-date"
            : "bg-gray-100 print:bg-transparent",
      )}
      data-testid="checkout-line-item"
    >
      <div className="checkout-list-item-content p-4">
        <div
          className={`checkout-list-item-title ${
            item.catalogueItem.type === "addOn" ? "self-center" : ""
          }`}
        >
          {reactStringReplace(item.catalogueItem.name, "+Live", (match, i) => (
            <span key={i} className="italic">
              +Live
            </span>
          ))}
        </div>
        {item.catalogueItem.type === "program" ||
        item.catalogueItem.type === "collegeCredit" ? (
          <div className="checkout-list-item-dates">
            <DateDisplay
              end={item.endDate}
              formattedEnd={item.formattedEndDate}
              start={item.startDate}
              formattedStart={item.formattedStartDate}
            />
          </div>
        ) : (
          <div className="checkout-list-item-dates" />
        )}
        <div
          className={`checkout-list-item-money ${
            item.discountCouponCode ? "" : "self-center"
          }`}
        >
          <div>
            <MoneyTable
              originalPrice={
                item.cartDiscount?.discountedPrice != null &&
                item.cartDiscount.discountedPrice !== item.retailPrice &&
                !item.discountCouponCode
                  ? item.formattedRetailPrice
                  : item.retailPrice !== item.priceToPay
                    ? item.formattedRetailPrice
                    : undefined
              }
              price={
                item.cartDiscount?.discountedPrice != null &&
                !item.discountCouponCode
                  ? formatCents(item.cartDiscount.discountedPrice)
                  : item.formattedPriceToPay
              }
              singleRow={true}
            />
            {item.discountCouponCode && (
              <div className="checkout-money-table">
                <div className="checkout-money-table-discount-code">
                  <span className="checkout-token">
                    {item.discountCouponCode}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {item.discountCouponCode || confirmDelete || changeDate ? null : (
          <div
            className="checkout-list-item-actions-space"
            aria-hidden="true"
          />
        )}

        {removeFn && !isDeleting && !confirmDelete && !changeDate ? (
          <div className="checkout-list-item-actions flex flex-row gap-3 pt-4 pb-1 border-t border-dashed border-gray-300">
            {canChangeDate ? (
              <button
                className="underline leading-none underline-offset-4 opacity-70"
                onClick={() => {
                  setChangeDate(true);
                }}
              >
                change dates
              </button>
            ) : null}

            <button
              className="underline leading-none underline-offset-4 opacity-70"
              data-testid="remove-button"
              onClick={removeStep1}
            >
              remove
            </button>
          </div>
        ) : null}

        {removeFn && confirmDelete ? (
          <>
            <LineItemConfirmDelete
              cancel={() => {
                setConfirmDelete(false);
              }}
              confirm={handleRemove}
              isDeleting={isDeleting}
            />
            {didError ? (
              <div className="col-span-3 flex items-center justify-center pt-2 font-bold text-red-600">
                <WarningCircleIcon className="mr-2" size={24} weight="fill" />
                We had trouble removing this item. Please try again.
              </div>
            ) : null}
          </>
        ) : null}

        {changeDate ? (
          <ChangeDate
            cancel={() => {
              setChangeDate(false);
            }}
            catalogueItemId={item.catalogueItem._id}
            currentCohortId={item._id}
          />
        ) : null}

        {errors?.length && !confirmDelete && !changeDate ? (
          <div className="checkout-list-item-errors">
            {errors.map((error, i) => {
              return (
                <div
                  key={i}
                  className="checkout-list-item-error max-w-max text-red-500 flex items-center gap-1 mt-1 leading-none pt-1 pr-2 pb-1"
                >
                  <WarningCircleIcon
                    color="currentColor"
                    size={20}
                    weight="fill"
                    className="block relative -left-1"
                  />
                  {error.message}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </li>
  );
}
